import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ArrowUp, CheckCircle2, ChevronDown, ChevronUp, File as IconoArchivo, Loader2, RotateCcw, X } from 'lucide-react';
import { useIsMobile } from '../../hooks/useIsMobile';
import { humanSize } from '../../lib/format';
import { DialogoConfirmar } from '../ui/confirmar';
import {
  cancelar, cancelarTodo, esActiva, limpiarTerminadas, reintentar, setAbierta, useTransferencias,
} from '../../lib/transferencias';
import './transferencias.css';

/*
 * Barra inferior de subidas de Drive (2026-10-08), como la de MEGA: progreso total, velocidad y tiempo restante,
 * y al desplegarla cada archivo con lo suyo (cancelar, reintentar). Montada en AppShell: sigue a la vista aunque
 * se salga de Drive mientras sube. Estado y reglas en lib/transferencias.js.
 */

const num = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 1 });
const velocidadTexto = (bps) => `${humanSize(bps).replace(/\.0 /, ' ')}/s`;

function restanteTexto(seg) {
  if (!Number.isFinite(seg) || seg <= 0) return '';
  if (seg < 60) return `faltan ${Math.max(1, Math.round(seg))} s`;
  if (seg < 3600) return `faltan ${Math.round(seg / 60)} min`;
  const h = Math.floor(seg / 3600);
  return `faltan ${h} h ${Math.round((seg % 3600) / 60)} min`;
}

function Anillo({ pct, estado }) {
  const r = 15;
  const c = 2 * Math.PI * r;
  if (estado === 'listo') return <CheckCircle2 className="tr-anillo-ico tr-ok" aria-hidden="true" />;
  if (estado === 'error') return <AlertTriangle className="tr-anillo-ico tr-mal" aria-hidden="true" />;
  return (
    <span className="tr-anillo" aria-hidden="true">
      <svg viewBox="0 0 36 36" width="36" height="36">
        <circle cx="18" cy="18" r={r} className="tr-anillo-fondo" />
        <circle cx="18" cy="18" r={r} className="tr-anillo-valor" strokeDasharray={c} strokeDashoffset={c * (1 - Math.min(1, pct))} />
      </svg>
      <ArrowUp className="tr-anillo-flecha" />
    </span>
  );
}

export default function BarraTransferencias() {
  const movil = useIsMobile();
  const { items, abierta } = useTransferencias();
  const [confirmar, setConfirmar] = useState(false);
  const [encima, setEncima] = useState(false);
  const [altoNav, setAltoNav] = useState(0);

  const visibles = useMemo(() => items.filter((t) => t.visible), [items]);
  const r = useMemo(() => {
    const activas = visibles.filter(esActiva);
    const cuentan = visibles.filter((t) => t.estado !== 'cancelado' && t.estado !== 'error');
    const total = cuentan.reduce((n, t) => n + t.tamano, 0);
    const cargado = cuentan.reduce((n, t) => n + (t.estado === 'listo' ? t.tamano : t.cargado), 0);
    const velocidad = activas.reduce((n, t) => n + (t.estado === 'subiendo' ? t.velocidad : 0), 0);
    return {
      activas,
      total,
      cargado,
      velocidad,
      pct: total ? cargado / total : activas.length ? 0 : 1,
      actual: activas.find((t) => t.estado !== 'en_cola') || activas[0],
      listos: visibles.filter((t) => t.estado === 'listo').length,
      errores: visibles.filter((t) => t.estado === 'error').length,
      cancelados: visibles.filter((t) => t.estado === 'cancelado').length,
      destino: visibles[visibles.length - 1]?.destino,
    };
  }, [visibles]);
  const enCurso = r.activas.length > 0;

  // Cerrar la pestaña a media subida la corta: el navegador pregunta antes
  useEffect(() => {
    if (!items.some(esActiva)) return undefined;
    const avisar = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', avisar);
    return () => window.removeEventListener('beforeunload', avisar);
  }, [items]);

  // Todo subido y sin errores: la barra se retira sola (si no se está mirando)
  useEffect(() => {
    if (!visibles.length || enCurso || r.errores || abierta || encima) return undefined;
    const t = setTimeout(limpiarTerminadas, 6000);
    return () => clearTimeout(t);
  }, [visibles.length, enCurso, r.errores, abierta, encima]);

  // En el teléfono va justo encima de la barra de navegación de abajo; los botones flotantes suben con ella
  useEffect(() => {
    if (!movil) { setAltoNav(0); return undefined; }
    const nav = document.querySelector('[data-testid="mobile-bottom-nav"]');
    if (!nav) { setAltoNav(0); return undefined; }
    const medir = () => setAltoNav(Math.round(nav.getBoundingClientRect().height));
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(nav);
    return () => ro.disconnect();
  }, [movil, visibles.length]);
  useEffect(() => {
    const raiz = document.documentElement;
    if (visibles.length) raiz.classList.add('con-transferencias');
    else raiz.classList.remove('con-transferencias');
    return () => raiz.classList.remove('con-transferencias');
  }, [visibles.length]);

  if (!visibles.length) return null;

  const n = visibles.length;
  const hechos = r.listos + r.errores + r.cancelados;
  let titulo;
  if (enCurso) {
    if (r.actual?.estado === 'procesando' && r.activas.length === 1) titulo = n === 1 ? `Guardando «${r.actual.nombre}» en Drive…` : `Guardando en Drive (${n} de ${n})…`;
    else titulo = n === 1 ? `Subiendo «${r.actual?.nombre}»` : `Subiendo ${Math.min(n, hechos + 1)} de ${n}`;
  } else if (r.listos === 0 && r.errores === 0) {
    titulo = 'Subida cancelada';
  } else if (r.errores) {
    titulo = `${r.listos} ${r.listos === 1 ? 'subido' : 'subidos'} · ${r.errores} con error`;
  } else {
    titulo = `${r.listos === 1 ? '1 archivo subido' : `${r.listos} archivos subidos`}${r.destino ? ` a ${r.destino}` : ''}`;
  }
  const pctTexto = `${num.format(Math.floor(r.pct * 1000) / 10)} %`;
  const detalle = enCurso
    ? [pctTexto, `${humanSize(r.cargado)} de ${humanSize(r.total)}`,
      r.velocidad > 0 ? velocidadTexto(r.velocidad) : null,
      r.velocidad > 0 ? restanteTexto((r.total - r.cargado) / r.velocidad) : null].filter(Boolean).join(' · ')
    : r.errores ? 'Despliega para ver qué pasó y reintentar' : `${humanSize(r.total)} en total`;
  const estadoGeneral = enCurso ? 'subiendo' : r.errores ? 'error' : r.listos ? 'listo' : 'cancelado';

  return (
    <>
      <section
        className={`tr-barra ${movil ? 'tr-barra--movil' : ''} ${abierta ? 'is-abierta' : ''} tr-${estadoGeneral}`}
        style={movil ? { bottom: altoNav } : undefined}
        aria-label="Subidas de Drive"
        data-testid="barra-transferencias"
        onMouseEnter={() => setEncima(true)}
        onMouseLeave={() => setEncima(false)}
      >
        {abierta && (
          <div className="tr-lista od-scroll" role="list" data-testid="transferencias-lista">
            <div className="tr-lista-cab">
              <span>Subidas</span>
              {enCurso
                ? <button type="button" className="tr-enlace tr-enlace--peligro" onClick={() => setConfirmar(true)}>Detener todo</button>
                : <button type="button" className="tr-enlace" onClick={limpiarTerminadas}>Limpiar</button>}
            </div>
            {visibles.map((t) => <Fila key={t.id} t={t} />)}
          </div>
        )}
        <div className="tr-fila-principal">
          <Anillo pct={r.pct} estado={estadoGeneral} />
          <button type="button" className="tr-resumen" onClick={() => setAbierta(!abierta)} aria-expanded={abierta} data-testid="transferencias-resumen">
            <span className="tr-titulo truncate">{titulo}</span>
            <span className="tr-detalle truncate" aria-live="polite">{detalle}</span>
            {enCurso && (
              <span className="tr-progreso" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(r.pct * 100)}>
                <span style={{ width: `${Math.max(2, r.pct * 100)}%` }} />
              </span>
            )}
          </button>
          <button type="button" className="tr-icono" onClick={() => setAbierta(!abierta)} aria-label={abierta ? 'Ocultar detalles' : 'Ver cada archivo'} title={abierta ? 'Ocultar detalles' : 'Ver cada archivo'}>
            {abierta ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
          </button>
          {enCurso ? (
            <button type="button" className="tr-icono" onClick={() => setConfirmar(true)} aria-label="Detener las subidas" title="Detener las subidas" data-testid="transferencias-detener"><X className="h-4 w-4" /></button>
          ) : (
            <button type="button" className="tr-icono" onClick={limpiarTerminadas} aria-label="Cerrar" title="Cerrar" data-testid="transferencias-cerrar"><X className="h-4 w-4" /></button>
          )}
        </div>
      </section>
      <DialogoConfirmar
        open={confirmar}
        onOpenChange={setConfirmar}
        titulo="¿Detener las subidas?"
        mensaje={`${r.activas.length === 1 ? 'El archivo que falta no se subirá' : `Los ${r.activas.length} archivos que faltan no se subirán`}. Lo que ya terminó se queda en Drive.`}
        boton="Detener"
        onConfirmar={() => { cancelarTodo(); setConfirmar(false); }}
        testId="transferencias-confirmar"
      />
    </>
  );
}

function Fila({ t }) {
  const pct = t.tamano ? Math.min(1, (t.estado === 'listo' ? t.tamano : t.cargado) / t.tamano) : t.estado === 'listo' ? 1 : 0;
  let estado;
  if (t.estado === 'en_cola') estado = 'En cola';
  else if (t.estado === 'subiendo') {
    estado = [`${Math.floor(pct * 100)} %`, t.velocidad > 0 ? velocidadTexto(t.velocidad) : null,
      t.velocidad > 0 ? restanteTexto((t.tamano - t.cargado) / t.velocidad) : null].filter(Boolean).join(' · ');
  } else if (t.estado === 'procesando') estado = 'Guardando en Drive…';
  else if (t.estado === 'listo') estado = t.destino ? `Subido a ${t.destino}` : 'Subido';
  else if (t.estado === 'cancelado') estado = 'Cancelado';
  else estado = t.error || 'No se pudo subir';
  return (
    <div className={`tr-item tr-item--${t.estado}`} role="listitem" data-testid="transferencia">
      <span className="tr-item-ico" aria-hidden="true">
        {t.estado === 'listo' ? <CheckCircle2 className="h-4 w-4" />
          : t.estado === 'error' ? <AlertTriangle className="h-4 w-4" />
            : t.estado === 'procesando' ? <Loader2 className="h-4 w-4 od-spin" />
              : <IconoArchivo className="h-4 w-4" />}
      </span>
      <div className="tr-item-cuerpo">
        <div className="tr-item-nombre">
          <span className="truncate" title={t.nombre}>{t.nombre}</span>
          <span className="tr-item-peso">{humanSize(t.tamano)}</span>
        </div>
        {(t.estado === 'subiendo' || t.estado === 'procesando') && (
          <span className="tr-progreso tr-progreso--fino"><span style={{ width: `${Math.max(2, pct * 100)}%` }} /></span>
        )}
        <div className="tr-item-estado truncate" title={estado}>{estado}</div>
      </div>
      {esActiva(t) ? (
        <button type="button" className="tr-icono" onClick={() => cancelar(t.id)} aria-label={`Cancelar ${t.nombre}`} title="Cancelar"><X className="h-4 w-4" /></button>
      ) : (t.estado === 'error' || t.estado === 'cancelado') && !/512 MB/.test(t.error || '') ? (
        <button type="button" className="tr-icono" onClick={() => reintentar(t.id)} aria-label={`Reintentar ${t.nombre}`} title="Reintentar"><RotateCcw className="h-4 w-4" /></button>
      ) : null}
    </div>
  );
}
